import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const API = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "ih_admin_token";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const AdminInbox = () => {
  const [password, setPassword] = useState("");
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [loginError, setLoginError] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  const [data, setData] = useState(null);
  const [tab, setTab] = useState("passport");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [expanded, setExpanded] = useState({});
  const [lightbox, setLightbox] = useState(null); // src string or null
  const [photosById, setPhotosById] = useState({}); // submission_id -> {place_photos, self_photo}
  const [photosLoading, setPhotosLoading] = useState({}); // submission_id -> bool

  const fetchPhotos = async (id) => {
    if (photosById[id] || photosLoading[id]) return;
    setPhotosLoading((x) => ({ ...x, [id]: true }));
    try {
      const res = await fetch(`${API}/api/admin/submission/${id}/photos`, {
        headers: { "X-Admin-Password": token },
      });
      if (res.ok) {
        const json = await res.json();
        setPhotosById((x) => ({ ...x, [id]: json }));
      }
    } catch (_) {
      // silent fail; user can retry by re-expanding
    } finally {
      setPhotosLoading((x) => ({ ...x, [id]: false }));
    }
  };

  const toggleExpand = (s) => {
    const nowOpen = !expanded[s.id];
    setExpanded((x) => ({ ...x, [s.id]: nowOpen }));
    if (nowOpen && (s.place_photos_count > 0 || s.has_self_photo)) {
      fetchPhotos(s.id);
    }
  };

  const fetchInbox = async (pw) => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API}/api/admin/inbox`, {
        headers: { "X-Admin-Password": pw },
      });
      if (res.status === 401) {
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setError("Session expired. Please log in again.");
        return;
      }
      if (!res.ok) throw new Error("Failed to load");
      const json = await res.json();
      setData(json);
    } catch (e) {
      setError("Could not load inbox.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchInbox(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const onLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      const res = await fetch(`${API}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      if (!res.ok) {
        setLoginError("Incorrect password.");
        return;
      }
      localStorage.setItem(TOKEN_KEY, password);
      setToken(password);
      setPassword("");
    } catch {
      setLoginError("Could not connect. Try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  const onLogout = () => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  const downloadCsv = (which) => {
    const url = `${API}/api/admin/export/${which}.csv`;
    fetch(url, { headers: { "X-Admin-Password": token } })
      .then((r) => r.blob())
      .then((blob) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(blob);
        a.download = which === "passport" ? "passport-submissions.csv" : "inner-hustle-subscribers.csv";
        a.click();
        URL.revokeObjectURL(a.href);
      });
  };

  // ---------- LOGIN VIEW ----------
  if (!token) {
    return (
      <div className="bg-[var(--ih-paper)] min-h-screen ih-grain flex items-center justify-center px-6">
        <form
          onSubmit={onLogin}
          className="w-full max-w-md bg-[var(--ih-cream)] border border-[var(--ih-ink)]/12 p-10"
          data-testid="admin-login-form"
        >
          <Link to="/" className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link">
            ← The Inner Hustle
          </Link>
          <div className="mt-6 font-mono-ui text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--ih-accent)" }}>
            Private — Editor's Inbox
          </div>
          <h1 className="mt-3 font-marquee leading-[1.05]" style={{ fontSize: "clamp(1.8rem, 3.4vw, 2.6rem)" }}>
            Sign in.
          </h1>
          <p className="mt-3 font-display italic text-[1rem] text-[var(--ih-muted)]">
            For Laurie's eyes only.
          </p>

          <label className="block mt-7">
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              className="mt-2 w-full bg-transparent border-b border-[var(--ih-ink)]/30 focus:border-[var(--ih-accent)] outline-none py-2 font-display text-[1.1rem] text-[var(--ih-ink)] transition-colors"
              data-testid="admin-password-input"
            />
          </label>

          {loginError && (
            <p className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--ih-accent)" }} data-testid="admin-login-error">
              {loginError}
            </p>
          )}

          <button
            type="submit"
            disabled={loginLoading}
            className="mt-7 ih-btn ih-btn--solid w-full disabled:opacity-50"
            data-testid="admin-login-submit"
          >
            {loginLoading ? "Signing in…" : "Enter →"}
          </button>
        </form>
      </div>
    );
  }

  // ---------- INBOX VIEW ----------
  const submissions = data?.passport_submissions || [];
  const subscribers = data?.subscribers || [];

  return (
    <div className="bg-[var(--ih-paper)] min-h-screen ih-grain">
      <header className="max-w-[1200px] mx-auto px-6 md:px-10 pt-10 pb-6 flex items-center justify-between">
        <div>
          <Link to="/" className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link">
            ← The Inner Hustle
          </Link>
          <div className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.42em]" style={{ color: "var(--ih-accent)" }}>
            Editor's Inbox
          </div>
          <h1 className="mt-2 font-marquee leading-[1.0]" style={{ fontSize: "clamp(2rem, 4.5vw, 3.4rem)" }}>
            Inbox.
          </h1>
        </div>
        <button onClick={onLogout} className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link" data-testid="admin-logout">
          Sign out →
        </button>
      </header>

      <div className="max-w-[1200px] mx-auto px-6 md:px-10">
        <div className="ih-rule" />

        {/* Tabs */}
        <div className="mt-8 flex items-center gap-8 border-b border-[var(--ih-ink)]/12">
          <button
            onClick={() => setTab("passport")}
            className={`pb-3 font-mono-ui text-[11px] uppercase tracking-[0.32em] border-b-2 transition-colors ${
              tab === "passport" ? "border-[var(--ih-accent)] text-[var(--ih-ink)]" : "border-transparent text-[var(--ih-muted)]"
            }`}
            data-testid="tab-passport"
          >
            Passport entries · {submissions.length}
          </button>
          <button
            onClick={() => setTab("subscribers")}
            className={`pb-3 font-mono-ui text-[11px] uppercase tracking-[0.32em] border-b-2 transition-colors ${
              tab === "subscribers" ? "border-[var(--ih-accent)] text-[var(--ih-ink)]" : "border-transparent text-[var(--ih-muted)]"
            }`}
            data-testid="tab-subscribers"
          >
            Circle subscribers · {subscribers.length}
          </button>
          <div className="ml-auto">
            <button
              onClick={() => downloadCsv(tab === "passport" ? "passport" : "subscribers")}
              className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link"
              data-testid="export-csv"
            >
              ↓ Export CSV
            </button>
          </div>
        </div>

        {loading && (
          <p className="mt-10 font-display italic text-[var(--ih-muted)]">Loading…</p>
        )}
        {error && (
          <p className="mt-10 font-mono-ui text-[10px] uppercase tracking-[0.32em]" style={{ color: "var(--ih-accent)" }}>{error}</p>
        )}

        {/* Passport entries */}
        {tab === "passport" && !loading && (
          <div className="mt-8 pb-24" data-testid="passport-list">
            {submissions.length === 0 ? (
              <p className="font-display italic text-[1.1rem] text-[var(--ih-muted)] py-12">
                No entries yet. When someone submits a Passport to Peace reflection, it'll land here.
              </p>
            ) : (
              <ul className="divide-y divide-[var(--ih-ink)]/10">
                {submissions.map((s) => (
                  <li key={s.id} className="py-7">
                    <div className="flex items-baseline justify-between gap-4 flex-wrap">
                      <div>
                        <h3 className="font-marquee text-[1.6rem] md:text-[2rem] leading-[1.15]">
                          {s.name} <span className="text-[var(--ih-muted)] font-display italic text-[1.1rem]">— {s.place}{s.location ? `, ${s.location}` : ""}</span>
                        </h3>
                        <div className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
                          {fmtDate(s.created_at)} · <a href={`mailto:${s.email}`} className="ih-link" style={{ color: "var(--ih-accent)" }}>{s.email}</a>
                        </div>
                      </div>
                      <button
                        onClick={() => toggleExpand(s)}
                        className="font-mono-ui text-[10px] uppercase tracking-[0.32em] ih-link"
                      >
                        {expanded[s.id] ? "Collapse ↑" : "Read full ↓"}
                      </button>
                    </div>
                    <p
                      className={`mt-4 font-display leading-[1.7] text-[var(--ih-ink-soft)] text-[1.02rem] ${
                        expanded[s.id] ? "" : "line-clamp-3"
                      }`}
                      style={
                        expanded[s.id]
                          ? { whiteSpace: "pre-wrap" }
                          : { display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }
                      }
                    >
                      {s.reflection}
                    </p>

                    {(s.place_photos_count > 0 || s.has_self_photo) && (
                      <div className="mt-5">
                        {!expanded[s.id] ? (
                          <div className="font-mono-ui text-[9px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
                            {s.place_photos_count > 0 && (
                              <>
                                {s.place_photos_count} place photo{s.place_photos_count > 1 ? "s" : ""}
                                {s.has_self_photo && " · "}
                              </>
                            )}
                            {s.has_self_photo && <>1 self portrait</>}
                            {" · expand to view"}
                          </div>
                        ) : photosLoading[s.id] ? (
                          <div className="font-mono-ui text-[9px] uppercase tracking-[0.32em] text-[var(--ih-muted)] py-3">
                            Loading photos…
                          </div>
                        ) : photosById[s.id] ? (
                          <>
                            {photosById[s.id].place_photos?.length > 0 && (
                              <>
                                <div className="font-mono-ui text-[9px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
                                  Place — {photosById[s.id].place_photos.length} photo{photosById[s.id].place_photos.length > 1 ? "s" : ""}
                                </div>
                                <div className="mt-2 flex gap-3 flex-wrap">
                                  {photosById[s.id].place_photos.map((src, i) => (
                                    <button
                                      key={i}
                                      type="button"
                                      onClick={() => setLightbox(src)}
                                      className="block w-28 h-28 overflow-hidden border border-[var(--ih-ink)]/15 hover:border-[var(--ih-accent)] transition-colors"
                                    >
                                      <img src={src} alt={`${s.place} ${i + 1}`} className="w-full h-full object-cover" />
                                    </button>
                                  ))}
                                </div>
                              </>
                            )}
                            {photosById[s.id].self_photo && (
                              <div className="mt-4">
                                <div className="font-mono-ui text-[9px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">
                                  Self portrait
                                </div>
                                <button
                                  type="button"
                                  onClick={() => setLightbox(photosById[s.id].self_photo)}
                                  className="mt-2 block w-28 h-28 overflow-hidden border border-[var(--ih-ink)]/15 hover:border-[var(--ih-accent)] transition-colors"
                                >
                                  <img src={photosById[s.id].self_photo} alt={`${s.name} in ${s.place}`} className="w-full h-full object-cover" />
                                </button>
                              </div>
                            )}
                          </>
                        ) : null}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}

        {/* Subscribers */}
        {tab === "subscribers" && !loading && (
          <div className="mt-8 pb-24" data-testid="subscribers-list">
            {subscribers.length === 0 ? (
              <p className="font-display italic text-[1.1rem] text-[var(--ih-muted)] py-12">
                No subscribers yet. When someone joins the Inner Hustle Circle, they'll appear here.
              </p>
            ) : (
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-[var(--ih-ink)]/15">
                    <th className="py-3 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">Email</th>
                    <th className="py-3 font-mono-ui text-[10px] uppercase tracking-[0.32em] text-[var(--ih-muted)]">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.map((s) => (
                    <tr key={s.email} className="border-b border-[var(--ih-ink)]/8">
                      <td className="py-4 font-display text-[1.05rem]">
                        <a href={`mailto:${s.email}`} className="ih-link" style={{ color: "var(--ih-accent)" }}>
                          {s.email}
                        </a>
                      </td>
                      <td className="py-4 font-mono-ui text-[11px] uppercase tracking-[0.22em] text-[var(--ih-muted)]">
                        {fmtDate(s.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-6 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <img src={lightbox} alt="Full view" className="max-w-full max-h-full object-contain" />
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute top-6 right-6 text-white font-mono-ui text-[10px] uppercase tracking-[0.32em]"
          >
            Close ×
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminInbox;
